import { Loader, SdirButton } from '@sdir/sds';
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import moment from 'moment';
import styled from 'styled-components';
import { ErrorMessage } from '@sdir/utilities/lib/components';
// eslint-disable-next-line max-len
import { PersonalCertificateVerificationResultV3 } from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import { BackLink, Certificate, FormDate, FormInput } from '@sdir/blueprint.aps/lib/components';
import { CertificateProps } from '@sdir/blueprint.aps/lib/components/Organisms/Certificate';
import { formatFullName } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { personalCertificateVerifyApi } from '../../../httpclient';
import { CapitalizeString } from '../../../helpers/certificateHelpers';
import { routes } from '../../../constants/routes';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

type InitialValues = {
  dateOfBirth: string;
  certificateNumber: string;
};

const maxDate = new Date(2500, 11, 31);

const SearchForm: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const [certificate, setCertificate] = useState<null | PersonalCertificateVerificationResultV3>();
  const [searchError, setSearchError] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);

  const initialValues: InitialValues = {
    dateOfBirth: '',
    certificateNumber: '',
  };

  const handleSubmit = useCallback(
    (values: InitialValues) => {
      const shortDateOfBirth = values?.dateOfBirth
        ? moment(values?.dateOfBirth).format('YYYY-MM-DD')
        : '';
      setLoading(true);
      personalCertificateVerifyApi
        .v3PersonalcertificateverifyVerifybynumberandidentityv3DataPost({
          certificateNumber: values.certificateNumber,
          dateofBirth: shortDateOfBirth,
        })
        .then((response) => {
          if (response.status === 200) {
            setCertificate(response.data);
            setSearchError(undefined);
          }
          setLoading(false);
        })
        .catch((error) => {
          const { status } = error?.response || {};
          if (status >= 400 && status < 500) {
            setSearchError(intl.formatMessage({ id: 'certificate.notfound' }));
          } else {
            setSearchError(error);
          }
          setLoading(false);
        });
    },
    [intl]
  );

  const certificateProps: CertificateProps = useMemo(() => {
    const seafarerName = formatFullName(
      certificate?.seafarerFirstName || '',
      certificate?.seafarerMiddleName || '',
      certificate?.seafarerLastName || ''
    );
    const positionList =
      certificate?.limitations?.map((l) => {
        return {
          position: l.capacity || '',
          limitations: l.limit ? [CapitalizeString(l.limit)] : [],
        };
      }) || [];
    return {
      certificateName: certificate?.certificateName || '',
      authorizedOfficial: certificate?.authorizedOfficial || '',
      issuingAuthority: certificate?.issuingAuthority,
      dateOfBirth: certificate?.dateOfBirth || '',
      name: seafarerName,
      minimumRequirements: certificate?.minimumRequirements || '',
      accessibilityStatement: certificate?.accessibilityRequirement || '',
      limitationStatement: certificate?.capacityLimitationStatement || '',
      meetsRequirementStatement: certificate?.meetsRequirementUntil || '',
      positionList: positionList || [],
      qrData: '',
      qrImageLoading: false,
      certificateNr: certificate?.certificateNumber || '',
      hideLanguageSelector: true,
      hideQr: true,
      issuedDate: certificate?.issuedAt,
      validFrom: certificate?.validFrom || '',
    };
  }, [certificate]);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      dateOfBirth: Yup.string().required(intl.formatMessage({ id: 'required.field' })),
      certificateNumber: Yup.string().required(intl.formatMessage({ id: 'required.field' })),
    });
  }, [intl]);

  return (
    <>
      <BackLink
        text={intl.formatMessage({ id: 'link.back.title' })}
        onClick={() => navigate(routes.home)}
      />
      <h1>{intl.formatMessage({ id: 'certificate.search.header' })}</h1>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {() => (
          <StyledForm>
            <Field
              label={intl.formatMessage({ id: 'birthnumber' })}
              name="dateOfBirth"
              component={FormDate}
              locale={intl?.locale || 'no'}
              format="dd.MM.yyyy"
              maxDate={maxDate}
              showYearDropdown
              showMonthDropdown
            />
            <Field
              label={intl.formatMessage({ id: 'certificate.number' })}
              name="certificateNumber"
              component={FormInput}
            />
            <StyledSdirButton
              htmlType="submit"
              type="primary"
              text={intl.formatMessage({ id: 'search' })}
            />
            {loading && <Loader />}
            {searchError && <ErrorMessage error={searchError} />}
          </StyledForm>
        )}
      </Formik>
      {certificate && !searchError && (
        <CertificateWrapper translate="no">
          <Certificate {...certificateProps} />
        </CertificateWrapper>
      )}
    </>
  );
};

const StyledForm = styled(Form)`
  display: flex;
  grid-row-gap: 1em;
  flex-direction: column;
  max-width: 400px;
`;

const StyledSdirButton = styled(SdirButton)`
  width: fit-content;
`;

const CertificateWrapper = styled.div`
  margin: 20px 0;
  p {
    margin: 0;
  }
`;

export default SearchForm;
